/**
 * eSIM Card Renderer - Common utility for rendering eSIM collection cards
 * This file provides a unified way to render eSIM collection cards across different sections
 */

class EsimCardRenderer {
  /**
   * Render a collection card HTML
   * @param {Object} collection - Collection object with properties: title, url, image, min_price, description, maxDiscount
   * @param {Object} options - Optional configuration
   * @param {boolean} options.showDiscount - Whether to show discount badge (default: true)
   * @param {string} options.imageSize - Image size for flag (default: '64px')
   * @param {string} options.fallbackDescription - Fallback description if collection.description is empty
   * @returns {string} HTML string for the collection card
   */
  static renderCollectionCard(collection, options = {}) {
    const {
      showDiscount = true,
      imageSize = '64px',
      fallbackDescription = null
    } = options;

    // Generate discount badge if enabled and discount exists (similar to discount-badge-collection.liquid)
    const discountBadge = (showDiscount && collection.maxDiscount && Number(collection.maxDiscount) > 0)
      ? `<span class="badge badge-discount animate__animated animate__fadeIn" id="collection-discount-badge-${collection.id || collection.handle || collection.title}">
          ${collection.maxDiscount}% OFF
        </span>`
      : "";

    // Use fallback description if provided and collection description is empty
    const description = collection.description || fallbackDescription || 
      `Stay connected across ${collection.title} with fast, reliable data. Perfect for travelers or locals — no SIM card needed.`;

    // Create thumbnail image URL using collection-thumbnail-url logic
    // For global collections, always use thumbnail-global.jpg
    let thumbnailImage;
    if (collection.handle?.includes('global')) {
      thumbnailImage = 'thumbnail-global.webp';
    } else {
      // Use collection handle (remove 'esim-' prefix if exists)
      const cleanHandle = collection.handle?.replace('esim-', '') || '';
      thumbnailImage = `thumbnail-${cleanHandle}.webp`;
    }
    
    // Get the correct Shopify asset URL using the assets_url from Liquid templates
    // Replace the filename in the assets_url to get the thumbnail URL
    let assetUrl;
    
    // Debug logging
    // console.log('EsimCardRenderer - window.ShopifyTheme:', window.ShopifyTheme);
    // console.log('EsimCardRenderer - thumbnailImage:', thumbnailImage);
    
    if (window.ShopifyTheme?.assets_url && window.ShopifyTheme.assets_url !== '') {
      // Replace the filename in the assets_url with our thumbnail filename
      assetUrl = window.ShopifyTheme.assets_url.replace(/\/[^\/]+$/, `/${thumbnailImage}`);
      // console.log('EsimCardRenderer - using assets_url:', assetUrl);
    } else if (window.ShopifyTheme?.fallback_assets_url) {
      // Use fallback URL if assets_url is empty
      assetUrl = window.ShopifyTheme.fallback_assets_url + thumbnailImage;
      // console.log('EsimCardRenderer - using fallback_assets_url:', assetUrl);
    } else {
      // Final fallback
      assetUrl = `/assets/${thumbnailImage}`;
      // console.log('EsimCardRenderer - using final fallback:', assetUrl);
    }
    
    return `
      <div class="card-wrapper product-card-wrapper">
        <a href="${collection.url}" style="text-decoration: none;">
          <div class="esim-card">
            <div class="esim-card__header" style="background: url('${assetUrl}') lightgray 50% / cover no-repeat;">
              <div>
                ${discountBadge}
              </div>
              <img
                src="${collection.image}"
                alt="${collection.title}"
                class="collection-card__flag"
                loading="lazy"
                width="64px"
                height="64px"
              >
            </div>
            <div class="esim-card__body">
              <h3 class="esim-card__plan">${collection.title}</h3>
              <div class="esim-card__price">
                <span class="price-staring-from">${window.Shopify?.locales?.general?.esims?.price?.starting_from || 'Starting from'}</span>
                <div class="price-value price-item">${collection.min_price}</div>
              </div>
              <div class="esim-card__footer esim-card__footer_collection">
                <button onclick="window.location.href='${collection.url}'" class="button button--secondary esim-card__footer_child">
                  ${window.Shopify?.locales?.general?.esims?.ui?.view_plans || 'View Plans'}
                  <i class="ph ph-arrow-right"></i>
                </button>
              </div>
            </div>
          </div>
        </a>
      </div>
    `;
  }

  /**
   * Render multiple collection cards
   * @param {Array} collections - Array of collection objects
   * @param {Object} options - Options for rendering
   * @param {number} options.limit - Maximum number of cards to render (default: no limit)
   * @returns {string} HTML string for all collection cards
   */
  static renderCollectionCards(collections, options = {}) {
    const { limit } = options;
    const cardsToRender = limit ? collections.slice(0, limit) : collections;
    
    return cardsToRender.map(collection => 
      this.renderCollectionCard(collection, options)
    ).join('');
  }

  /**
   * Filter and render collections based on search query
   * @param {Array} collections - Array of all collections
   * @param {string} query - Search query
   * @param {Object} options - Options for rendering
   * @returns {string} HTML string for filtered collection cards
   */
  static filterAndRenderCollections(collections, query, options = {}) {
    const normalizedQuery = query?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    
    const filteredCollections = collections.filter(collection => 
      collection.title.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').includes(normalizedQuery) ||
      (collection.handle && collection.handle.replace(/-/g, " ").includes(normalizedQuery)) ||
      (collection.shortName && collection.shortName.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').includes(normalizedQuery)) ||
      (collection.isoCode && collection.isoCode.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').includes(normalizedQuery))
    );

    return this.renderCollectionCards(filteredCollections, options);
  }

  /**
   * Update container with rendered cards
   * @param {string} containerId - ID of the container element
   * @param {string} html - HTML string to insert
   */
  static updateContainer(containerId, html) {
    const container = document.getElementById(containerId);
    if (container) {
      container.innerHTML = html;
    }
  }

  /**
   * Show/hide containers based on search state
   * @param {string} defaultContainerId - ID of default container
   * @param {string} filteredContainerId - ID of filtered container
   * @param {boolean} hasQuery - Whether there's an active search query
   */
  static toggleSearchContainers(defaultContainerId, filteredContainerId, hasQuery) {
    const defaultContainer = document.getElementById(defaultContainerId);
    const filteredContainer = document.getElementById(filteredContainerId);

    if (defaultContainer) {
      defaultContainer.classList.toggle('hidden', hasQuery);
    }
    if (filteredContainer) {
      filteredContainer.classList.toggle('hidden', !hasQuery);
    }
  }
}

// Make it available globally
window.EsimCardRenderer = EsimCardRenderer; 